import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Edit2, Trash2, Clock, Users, Save, X, ChefHat, Flame, Shuffle } from "lucide-react";
import { SmartbouffeLogoSmall } from "../components/Logo";
import { Footer } from "../components/Footer";
import AdBanner from '../components/AdBanner';
import { WarningIcon, CheckIcon, FireIcon, ClockIcon, UsersIcon, MealIcon, FishIcon, VegetarianIcon, VeganIcon, CarnivoreIcon, StarIcon, ChartIcon, CommentIcon, ClockIcon as TimeIcon } from '../components/Icons';

// Helper pour les badges de régime alimentaire
const getDietBadge = (dietType) => {
  const badges = {
    vegan: { icon: <VeganIcon size={14} />, label: "Vegan", color: "#10B981" },
    vegetarian: { icon: <VegetarianIcon size={14} />, label: "Végétarien", color: "#87A96B" },
    pescatarian: { icon: <FishIcon size={14} />, label: "Pescatarien", color: "#3B82F6" },
    carnivore: { icon: <CarnivoreIcon size={14} />, label: "Carnivore", color: "#EF4444" },
    mixed: { icon: <MealIcon size={14} />, label: "Mixte", color: "#8B7355" }
  };
  return badges[dietType] || null;
};

export default function RecipeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [recipe, setRecipe] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editedRecipe, setEditedRecipe] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false); // Toggle entre original et modifié
  
  // États pour statut et commentaires
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);
  
  // États pour la compatibilité alimentaire
  const [compatibility, setCompatibility] = useState(null);
  const [showCompatibilityDialog, setShowCompatibilityDialog] = useState(false);
  const [checkingCompatibility, setCheckingCompatibility] = useState(false);
  
  // États pour le visualiseur d'images
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Informations de navigation depuis la recherche par ingrédients
  const fromIngredientSearch = location.state?.fromIngredientSearch;
  const searchResults = location.state?.searchResults || [];
  const selectedIngredients = location.state?.selectedIngredients || [];
  
  // Informations de navigation depuis "Inspire-moi!"
  const fromInspire = location.state?.fromInspire;
  const inspireStatus = location.state?.inspireStatus;

  useEffect(() => {
    loadCategories();
    loadRecipe();
  }, [id]);
  
  // Vérifier la compatibilité alimentaire après chargement de la recette
  useEffect(() => {
    if (recipe && id) {
      checkCompatibility();
    }
  }, [recipe?.id]);
  
  const checkCompatibility = async () => {
    try {
      setCheckingCompatibility(true);
      const response = await axios.get(`${API}/recipes/${id}/check-compatibility`, { withCredentials: true });
      setCompatibility(response.data);
      
      // Si la recette n'est pas compatible, afficher automatiquement le dialogue
      if (response.data && !response.data.compatible && response.data.warnings?.length > 0) {
        setShowCompatibilityDialog(true);
      }
    } catch (error) {
      setCheckingCompatibility(false);
    } finally {
      setCheckingCompatibility(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      setCategories([]);
    }
  };

  const loadRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}`, { withCredentials: true });
      const recipeData = response.data;
      
      // IMPORTANT: Utiliser ingredients_quebec (tasses/cuillères) pour les Québécois
      if (recipeData.ingredients_quebec && recipeData.ingredients_quebec.length > 0) {
        recipeData.ingredients = recipeData.ingredients_quebec;
      } else {
        recipeData.ingredients = recipeData.ingredients || [];
      }
      
      // S'assurer que les instructions existent
      if (!recipeData.instructions) {
        recipeData.instructions = [];
      }
      
      
      setRecipe(recipeData);
      setEditedRecipe(recipeData);
    } catch (error) {
      toast.error("Erreur lors du chargement de la recette");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(
        `${API}/recipes/${id}`,
        editedRecipe,
        { withCredentials: true }
      );
      
      // IMPORTANT: Recréer le champ virtuel ingredients après sauvegarde
      const updatedRecipe = response.data;
      if (!updatedRecipe.ingredients) {
        updatedRecipe.ingredients = updatedRecipe.ingredients_quebec || updatedRecipe.ingredients_metric || [];
      }
      if (!updatedRecipe.instructions) {
        updatedRecipe.instructions = [];
      }
      
      setRecipe(updatedRecipe);
      setEditing(false);
      toast.success("Recette mise à jour");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/recipes/${id}`, { withCredentials: true });
      toast.success("Recette supprimée");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleIngredientChange = (index, value) => {
    const newIngredients = [...(editedRecipe.ingredients || [])];
    newIngredients[index] = value;
    
    // Mettre à jour tous les champs d'ingrédients pour la cohérence
    setEditedRecipe({ 
      ...editedRecipe, 
      ingredients: newIngredients,
      ingredients_quebec: newIngredients,
      ingredients_metric: newIngredients // On garde la même valeur pour simplicité
    });
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...(editedRecipe.instructions || [])];
    newInstructions[index] = value;
    setEditedRecipe({ ...editedRecipe, instructions: newInstructions });
  };

  const addIngredient = () => {
    const currentIngredients = editedRecipe.ingredients || [];
    const newIngredients = [...currentIngredients, ""];
    setEditedRecipe({
      ...editedRecipe,
      ingredients: newIngredients,
      ingredients_quebec: newIngredients,
      ingredients_metric: newIngredients
    });
  };

  const removeIngredient = (index) => {
    const currentIngredients = editedRecipe.ingredients || [];
    const newIngredients = currentIngredients.filter((_, i) => i !== index);
    setEditedRecipe({
      ...editedRecipe,
      ingredients: newIngredients,
      ingredients_quebec: newIngredients,
      ingredients_metric: newIngredients
    });
  };

  const addInstruction = () => {
    const currentInstructions = editedRecipe.instructions || [];
    setEditedRecipe({
      ...editedRecipe,
      instructions: [...currentInstructions, ""]
    });
  };

  const removeInstruction = (index) => {
    const currentInstructions = editedRecipe.instructions || [];
    setEditedRecipe({
      ...editedRecipe,
      instructions: currentInstructions.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="loading-screen" data-testid="recipe-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(
        `${API}/recipes/${id}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      setRecipe({ ...recipe, status: newStatus });
      toast.success("Statut mis à jour!");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error("Le commentaire ne peut pas être vide");
      return;
    }

    setAddingComment(true);
    try {
      const response = await axios.post(
        `${API}/recipes/${id}/comments`,
        { text: newComment },
        { withCredentials: true }
      );
      
      // Mettre à jour la recette avec le nouveau commentaire
      const updatedComments = [...(recipe.comments || []), response.data.comment];
      setRecipe({ ...recipe, comments: updatedComments });
      setNewComment("");
      toast.success("Commentaire ajouté!");
    } catch (error) {
      toast.error("Erreur lors de l'ajout du commentaire");
    } finally {
      setAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(
        `${API}/recipes/${id}/comments/${commentId}`,
        { withCredentials: true }
      );
      
      // Mettre à jour la recette en retirant le commentaire
      const updatedComments = (recipe.comments || []).filter(c => c.id !== commentId);
      setRecipe({ ...recipe, comments: updatedComments });
      toast.success("Commentaire supprimé!");
    } catch (error) {
      toast.error("Erreur lors de la suppression du commentaire");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'à_faire': { label: 'À faire', icon: <TimeIcon size={14} color="#2196F3" />, color: '#2196F3' },
      'fait': { label: 'Fait', icon: <CheckIcon size={14} color="#4CAF50" />, color: '#4CAF50' },
      'à_refaire': { label: 'À refaire', icon: <StarIcon size={14} color="#FFC107" />, color: '#FFC107' }
    };
    return badges[status];
  };

  const handleShuffle = async () => {
    try {
      const statusParam = inspireStatus && inspireStatus !== 'tout' ? `?status=${inspireStatus}` : '';
      const response = await axios.get(
        `${API}/recipes/random${statusParam}`,
        { withCredentials: true }
      );
      
      // Naviguer vers la nouvelle recette en gardant le contexte "Inspire-moi"
      navigate(`/recipe/${response.data.id}`, {
        state: { fromInspire: true, inspireStatus: inspireStatus },
        replace: true
      });
      
      // Recharger la page pour afficher la nouvelle recette
      window.location.reload();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("Aucune autre recette trouvée");
      } else {
        toast.error("Erreur lors du changement de recette");
      }
    }
  };

  // Fonctions de zoom pour les images
  const handleZoomIn = () => {
    setImageScale(prev => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setImageScale(prev => Math.max(prev - 0.5, 1));
  };

  const handleResetZoom = () => {
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleImageChange = (newIndex) => {
    setCurrentImageIndex(newIndex);
    handleResetZoom();
  };

  const handleImageDoubleClick = () => {
    if (imageScale === 1) {
      setImageScale(2);
    } else {
      handleResetZoom();
    }
  };

  // Fonctions de déplacement de l'image
  const handleMouseDown = (e) => {
    if (imageScale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - imagePosition.x,
        y: e.clientY - imagePosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && imageScale > 1) {
      setImagePosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Support tactile pour mobile
  const handleTouchStart = (e) => {
    if (imageScale > 1 && e.touches.length === 1) {
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - imagePosition.x,
        y: e.touches[0].clientY - imagePosition.y
      });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && imageScale > 1 && e.touches.length === 1) {
      e.preventDefault();
      setImagePosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  if (!recipe) return null;

  return (
    <div className="recipe-detail" data-testid="recipe-detail">
      <div className="recipe-detail-header">
        <div className="header-left-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ 
              width: '40px', 
              height: '40px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <SmartbouffeLogoSmall style={{ width: '100%', height: '100%', display: 'block' }} />
            </div>
            <span style={{ 
              fontFamily: 'Georgia, serif',
              fontSize: '1.3rem',
              fontWeight: '600',
              color: '#3A4A3E',
              letterSpacing: '-0.02em'
            }}>
              smartbouffe
            </span>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => {
              if (fromIngredientSearch) {
                navigate("/dashboard", { 
                  state: { 
                    showSearchResults: true,
                    searchResults: searchResults,
                    selectedIngredients: selectedIngredients
                  }
                });
              } else {
                navigate("/dashboard");
              }
            }} 
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="mr-2" />
            {fromIngredientSearch ? "Retour aux suggestions" : "Retour"}
          </Button>
        </div>
        <div className="header-actions">
          {editing ? (
            <>
              <Button variant="outline" onClick={() => { setEditing(false); setEditedRecipe(recipe); }} data-testid="cancel-edit-btn">
                <X size={18} className="mr-2" />
                Annuler
              </Button>
              <Button onClick={handleSave} data-testid="save-btn">
                <Save size={18} className="mr-2" />
                Sauvegarder
              </Button>
            </>
          ) : (
            <>
              {fromInspire && (
                <Button 
                  onClick={handleShuffle}
                  style={{
                    background: '#87A96B',
                    color: 'white'
                  }}
                  data-testid="shuffle-btn"
                  title="Autre recette aléatoire"
                >
                  <Shuffle size={18} />
                </Button>
              )}
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-btn">
                <Edit2 size={18} className="mr-2" />
                Modifier
              </Button>
              <Button variant="destructive" onClick={() => setShowDeleteDialog(true)} data-testid="delete-btn">
                <Trash2 size={18} className="mr-2" />
                Supprimer
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="recipe-detail-content">
        {editing ? (
          <>
            <div className="form-group">
              <Label>Titre</Label>
              <Input
                value={editedRecipe.title}
                onChange={(e) => setEditedRecipe({ ...editedRecipe, title: e.target.value })}
                data-testid="edit-title-input"
              />
            </div>

            <div className="recipe-meta-edit">
              <div className="form-group">
                <Label>Catégorie</Label>
                <select
                  value={editedRecipe.category_id}
                  onChange={(e) => setEditedRecipe({ ...editedRecipe, category_id: e.target.value })}
                  className="category-select-edit"
                  data-testid="edit-category-select"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <Label>Temps de préparation</Label>
                <Input
                  value={editedRecipe.prep_time || ""}
                  onChange={(e) => setEditedRecipe({ ...editedRecipe, prep_time: e.target.value })}
                  placeholder="ex: 15 min"
                  data-testid="edit-prep-time-input"
                />
              </div>
              <div className="form-group">
                <Label>Temps de cuisson</Label>
                <Input
                  value={editedRecipe.cook_time || ""}
                  onChange={(e) => setEditedRecipe({ ...editedRecipe, cook_time: e.target.value })}
                  placeholder="ex: 30 min"
                  data-testid="edit-cook-time-input"
                />
              </div>
              <div className="form-group">
                <Label>Portions</Label>
                <Input
                  value={editedRecipe.servings || ""}
                  onChange={(e) => setEditedRecipe({ ...editedRecipe, servings: e.target.value })}
                  placeholder="ex: 4 personnes"
                  data-testid="edit-servings-input"
                />
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <Label>Ingrédients</Label>
                <Button size="sm" variant="outline" onClick={addIngredient} data-testid="add-ingredient-btn">
                  + Ajouter
                </Button>
              </div>
              {(editedRecipe.ingredients || []).map((ingredient, index) => (
                <div key={`edit-item-${index}`} className="list-item-edit">
                  <Input
                    value={ingredient || ""}
                    onChange={(e) => handleIngredientChange(index, e.target.value)}
                    data-testid={`ingredient-input-${index}`}
                  />
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => removeIngredient(index)}
                    data-testid={`remove-ingredient-${index}`}
                  >
                    <X size={18} />
                  </Button>
                </div>
              ))}
            </div>

            <div className="form-section">
              <div className="section-header">
                <Label>Instructions</Label>
                <Button size="sm" variant="outline" onClick={addInstruction} data-testid="add-instruction-btn">
                  + Ajouter
                </Button>
              </div>
              {(editedRecipe.instructions || []).map((instruction, index) => (
                <div key={`edit-item-${index}`} className="list-item-edit">
                  <Textarea
                    value={instruction || ""}
                    onChange={(e) => handleInstructionChange(index, e.target.value)}
                    rows={2}
                    data-testid={`instruction-input-${index}`}
                  />
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => removeInstruction(index)}
                    data-testid={`remove-instruction-${index}`}
                  >
                    <X size={18} />
                  </Button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
              <h1 className="recipe-title" data-testid="recipe-title" style={{ margin: 0 }}>{recipe.title}</h1>
              {recipe.has_modifications && (
                <span style={{
                  background: '#FFA500',
                  color: 'white',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.3rem'
                }}>
                  ✏️ Version modifiée
                </span>
              )}
            </div>
            
            {recipe.creator_name && (
              <p className="recipe-creator" style={{ 
                fontSize: '0.9rem', 
                color: '#8B7355', 
                marginTop: '0.5rem', 
                marginBottom: '1rem',
                fontStyle: 'italic'
              }}>
                Par {recipe.creator_name}
              </p>
            )}
            
            {/* Image du plat extraite de la vidéo */}
            {recipe.recipe_image_url && !recipe.recipe_image_url.includes('unsplash') && (
              <div style={{ marginBottom: '1.5rem' }}>
                <img 
                  src={recipe.recipe_image_url.startsWith('http') ? recipe.recipe_image_url : API + recipe.recipe_image_url}
                  alt={recipe.title}
                  style={{
                    width: '100%',
                    maxWidth: '280px',
                    height: 'auto',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: '2px solid #D4A574',
                    marginBottom: '1rem'
                  }}
                />
              </div>
            )}
            
            {recipe.video_source_url && (
              <div style={{ marginBottom: '1.5rem' }}>
                <a 
                  href={recipe.video_source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#D4A574',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                    border: '1px solid #D4A574',
                    borderRadius: '8px',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#D4A574';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#D4A574';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="23 7 16 12 23 17 23 7"></polygon>
                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                  </svg>
                  Voir la vidéo originale
                </a>
              </div>
            )}
            
            {recipe.source_images && recipe.source_images.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <button 
                  onClick={() => {
                    setCurrentImageIndex(0);
                    setShowImageViewer(true);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#D4A574',
                    textDecoration: 'none',
                    fontSize: '0.9rem',
                    padding: '0.5rem 1rem',
                    border: '1px solid #D4A574',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    background: 'transparent',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#D4A574';
                    e.currentTarget.style.color = '#FFFFFF';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#D4A574';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  Voir {recipe.source_images.length > 1 ? 'les images sources' : "l'image source"} ({recipe.source_images.length})
                </button>
              </div>
            )}
            
            <div className="recipe-meta">
              {categories.find(c => c.id === recipe.category_id) && (
                <span className="recipe-category" data-testid="recipe-category">
                  {categories.find(c => c.id === recipe.category_id).name}
                </span>
              )}
              {recipe.diet_type && getDietBadge(recipe.diet_type) && (
                <div className="meta-item" data-testid="diet-type" style={{
                  background: getDietBadge(recipe.diet_type).color,
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: '500',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{display: 'inline-flex', alignItems: 'center'}}>{getDietBadge(recipe.diet_type).icon}</span>
                  <span>{getDietBadge(recipe.diet_type).label}</span>
                </div>
              )}
              {recipe.contains_allergens && recipe.contains_allergens.length > 0 && (
                <div className="meta-item" data-testid="allergen-badge" style={{
                  background: '#DC3545',
                  color: 'white',
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'help'
                }} title={`Contient: ${recipe.contains_allergens.join(', ')}`}>
                  <WarningIcon size={20} color="#FFA726" />
                  <span>Contient allergènes: {recipe.contains_allergens.join(', ')}</span>
                </div>
              )}
              {recipe.prep_time && (
                <div className="meta-item" data-testid="prep-time">
                  <ChefHat size={18} />
                  <span>Préparation: {recipe.prep_time}</span>
                </div>
              )}
              {recipe.cook_time && (
                <div className="meta-item" data-testid="cook-time">
                  <Flame size={18} />
                  <span>Cuisson: {recipe.cook_time}</span>
                </div>
              )}
              {recipe.servings && (
                <div className="meta-item" data-testid="servings">
                  <Users size={18} />
                  <span>{recipe.servings}</span>
                </div>
              )}
            </div>

            <div className="recipe-section">
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                marginBottom: '1rem',
                flexWrap: 'wrap',
                gap: '0.75rem'
              }}>
                <h2 style={{ margin: 0 }}>Ingrédients</h2>
                
                {recipe.has_modifications && recipe.original_ingredients_quebec && (
                  <div className="version-toggle" style={{
                    display: 'inline-flex',
                    background: '#F5F1E8',
                    borderRadius: '8px',
                    padding: '0.25rem',
                    gap: '0.25rem',
                    flexShrink: 0
                  }}>
                    <button
                      onClick={() => setShowOriginal(false)}
                      className="toggle-btn"
                      style={{
                        padding: '0.5rem 0.75rem',
                        border: 'none',
                        borderRadius: '6px',
                        background: !showOriginal ? '#87A96B' : 'transparent',
                        color: !showOriginal ? 'white' : '#5D6F61',
                        fontWeight: '500',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Modifiée
                    </button>
                    <button
                      onClick={() => setShowOriginal(true)}
                      className="toggle-btn"
                      style={{
                        padding: '0.5rem 0.75rem',
                        border: 'none',
                        borderRadius: '6px',
                        background: showOriginal ? '#87A96B' : 'transparent',
                        color: showOriginal ? 'white' : '#5D6F61',
                        fontWeight: '500',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Originale
                    </button>
                  </div>
                )}
              </div>
              
              <ul className="ingredients-list">
                {(() => {
                  // Déterminer quels ingrédients afficher selon le toggle
                  let ingredientsToShow = [];
                  
                  if (recipe.has_modifications) {
                    if (showOriginal) {
                      // Afficher la version ORIGINALE
                      ingredientsToShow = recipe.original_ingredients_quebec || recipe.original_ingredients_metric || [];
                    } else {
                      // Afficher la version MODIFIÉE (courante)
                      ingredientsToShow = recipe.ingredients_quebec || recipe.ingredients_metric || recipe.ingredients || [];
                    }
                  } else {
                    // Pas de modifications - afficher les ingrédients normaux
                    ingredientsToShow = recipe.ingredients_quebec || recipe.ingredients_metric || recipe.ingredients || [];
                    
                    // Fallback aux originaux si vide (recette sans modifications mais avec originaux sauvegardés)
                    if ((!ingredientsToShow || ingredientsToShow.length === 0) && recipe.original_ingredients_quebec) {
                      ingredientsToShow = recipe.original_ingredients_quebec;
                    }
                  }
                  
                  return ingredientsToShow && ingredientsToShow.length > 0 ? (
                    ingredientsToShow.map((ingredient, index) => (
                      <li key={`ingredient-${index}`} data-testid={`ingredient-${index}`}>{ingredient}</li>
                    ))
                  ) : (
                    <p>Aucun ingrédient disponible</p>
                  );
                })()}
              </ul>
            </div>

            <div className="recipe-section">
              <h2>Instructions</h2>
              <ol className="instructions-list">
                {(() => {
                  // Déterminer quelles instructions afficher
                  let instructionsToShow = recipe.instructions;
                  
                  if (recipe.has_modifications && showOriginal && recipe.original_instructions) {
                    // Afficher la version originale
                    instructionsToShow = recipe.original_instructions;
                  }
                  
                  return instructionsToShow && instructionsToShow.length > 0 ? (
                    instructionsToShow.map((instruction, index) => (
                      <li key={`instruction-${index}`} data-testid={`instruction-${index}`}>{instruction}</li>
                    ))
                  ) : (
                    <p>Aucune instruction disponible</p>
                  );
                })()}
              </ol>
            </div>

            {/* Section Statut et Commentaires */}
            <div style={{
              marginTop: '3rem',
              padding: '2rem',
              background: '#F5F1E8',
              borderRadius: '12px',
              border: '2px solid #D4A574'
            }}>
              {/* Statut de la recette */}
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#8B7355',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <ChartIcon size={20} color="#8B7355" /> Statut de la recette
                </h3>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {['à_faire', 'fait', 'à_refaire'].map((status) => {
                    const badge = getStatusBadge(status);
                    const isActive = recipe.status === status;
                    return (
                      <button
                        key={status}
                        onClick={() => handleStatusChange(isActive ? null : status)}
                        style={{
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          border: isActive ? 'none' : '2px solid #D4A574',
                          background: isActive ? badge.color : 'white',
                          color: isActive ? 'white' : '#8B7355',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          fontSize: '0.95rem',
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = '#FFF9E6';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.background = 'white';
                          }
                        }}
                      >
                        <span style={{display: 'inline-flex', alignItems: 'center', gap: '0.5rem'}}>
                          <span style={{display: 'inline-flex'}}>{badge.icon}</span>
                          {badge.label}
                        </span>
                      </button>
                    );
                  })}
                  {recipe.status && (
                    <button
                      onClick={() => handleStatusChange(null)}
                      style={{
                        padding: '0.75rem 1.5rem',
                        borderRadius: '8px',
                        border: '2px solid #DC3545',
                        background: 'white',
                        color: '#DC3545',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.background = '#DC3545';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = '#DC3545';
                      }}
                    >
                      ✕ Retirer le statut
                    </button>
                  )}
                </div>
              </div>

              {/* Commentaires */}
              <div>
                <h3 style={{
                  fontSize: '1.3rem',
                  fontWeight: '600',
                  color: '#8B7355',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <CommentIcon size={20} color="#8B7355" /> Commentaires ({recipe.comments?.length || 0})
                </h3>
                
                {/* Liste des commentaires */}
                {recipe.comments && recipe.comments.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    {recipe.comments.map((comment) => (
                      <div key={comment.id} style={{
                        background: 'white',
                        padding: '1rem',
                        borderRadius: '8px',
                        marginBottom: '0.75rem',
                        border: '1px solid #D4A574'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <p style={{
                            color: '#3A4A3E',
                            fontSize: '0.95rem',
                            flex: 1,
                            marginRight: '1rem'
                          }}>
                            {comment.text}
                          </p>
                          <button
                            onClick={() => handleDeleteComment(comment.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#DC3545',
                              cursor: 'pointer',
                              padding: '0.25rem',
                              fontSize: '1.2rem'
                            }}
                            title="Supprimer le commentaire"
                          >
                            🗑️
                          </button>
                        </div>
                        <p style={{
                          fontSize: '0.8rem',
                          color: '#A0826D',
                          marginTop: '0.5rem'
                        }}>
                          {new Date(comment.created_at).toLocaleDateString('fr-CA', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Ajouter un commentaire */}
                <div>
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Ajouter un commentaire... (ex: Délicieux! Réduire le sel la prochaine fois)"
                    style={{
                      marginBottom: '0.75rem',
                      background: 'white',
                      border: '2px solid #D4A574',
                      minHeight: '100px'
                    }}
                  />
                  <Button
                    onClick={handleAddComment}
                    disabled={addingComment || !newComment.trim()}
                    style={{
                      background: '#87A96B',
                      color: 'white'
                    }}
                  >
                    {addingComment ? 'Ajout...' : 'Ajouter un commentaire'}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Dialog pour afficher les images sources avec zoom */}
      <Dialog open={showImageViewer} onOpenChange={(open) => {
        setShowImageViewer(open);
        if (!open) handleResetZoom();
      }}>
        <DialogContent style={{ 
          maxWidth: '95vw', 
          maxHeight: '95vh', 
          padding: '0.5rem',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <DialogHeader style={{ padding: '0.5rem 0' }}>
            <DialogTitle style={{ fontSize: '1rem', textAlign: 'center' }}>
              Image source {recipe?.source_images && recipe.source_images.length > 1 
                ? `${currentImageIndex + 1}/${recipe.source_images.length}` 
                : ''}
            </DialogTitle>
          </DialogHeader>
          
          {/* Container d'image avec zoom et pan */}
          <div 
            style={{ 
              flex: 1,
              overflow: 'hidden',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              minHeight: '300px',
              touchAction: imageScale > 1 ? 'none' : 'auto',
              cursor: imageScale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
            }}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {recipe?.source_images && recipe.source_images[currentImageIndex] && (
              <img 
                src={`${API}/recipes/images/${recipe.source_images[currentImageIndex].split('/').pop()}`}
                alt={`Image source ${currentImageIndex + 1}`}
                onDoubleClick={handleImageDoubleClick}
                onMouseDown={handleMouseDown}
                onTouchStart={handleTouchStart}
                style={{
                  maxWidth: imageScale === 1 ? '100%' : 'none',
                  maxHeight: imageScale === 1 ? '60vh' : 'none',
                  width: imageScale > 1 ? `${100 * imageScale}%` : 'auto',
                  objectFit: 'contain',
                  transform: `scale(${imageScale === 1 ? 1 : 1}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
                  cursor: imageScale === 1 ? 'zoom-in' : (isDragging ? 'grabbing' : 'grab'),
                  transition: isDragging ? 'none' : 'transform 0.2s ease',
                  userSelect: 'none',
                  pointerEvents: 'auto'
                }}
                draggable="false"
              />
            )}
          </div>

          {/* Contrôles de zoom */}
          <div style={{ 
            display: 'flex',
            gap: '0.5rem',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '0.75rem 0',
            flexWrap: 'wrap'
          }}>
            <Button 
              variant="outline"
              onClick={handleZoomOut}
              disabled={imageScale <= 1}
              style={{ minWidth: '40px', padding: '0.5rem' }}
            >
              🔍−
            </Button>
            <span style={{ 
              fontSize: '0.85rem', 
              color: '#5D6F61',
              minWidth: '50px',
              textAlign: 'center'
            }}>
              {Math.round(imageScale * 100)}%
            </span>
            <Button 
              variant="outline"
              onClick={handleZoomIn}
              disabled={imageScale >= 3}
              style={{ minWidth: '40px', padding: '0.5rem' }}
            >
              🔍+
            </Button>
            {imageScale > 1 && (
              <Button 
                variant="outline"
                onClick={handleResetZoom}
                style={{ padding: '0.5rem 0.75rem', fontSize: '0.85rem' }}
              >
                Réinitialiser
              </Button>
            )}
          </div>

          {/* Navigation entre images */}
          {recipe?.source_images && recipe.source_images.length > 1 && (
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              justifyContent: 'center',
              paddingBottom: '0.5rem'
            }}>
              <Button 
                variant="outline"
                onClick={() => handleImageChange(Math.max(0, currentImageIndex - 1))}
                disabled={currentImageIndex === 0}
                style={{ flex: 1, maxWidth: '150px' }}
              >
                ← Précédent
              </Button>
              <Button 
                variant="outline"
                onClick={() => handleImageChange(Math.min(recipe.source_images.length - 1, currentImageIndex + 1))}
                disabled={currentImageIndex === recipe.source_images.length - 1}
                style={{ flex: 1, maxWidth: '150px' }}
              >
                Suivant →
              </Button>
            </div>
          )}
          
          <p style={{ 
            fontSize: '0.7rem', 
            color: '#888', 
            textAlign: 'center',
            margin: '0.25rem 0 0 0',
            lineHeight: '1.3'
          }}>
            Double-cliquez pour zoomer • Glissez pour déplacer
          </p>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer la recette?</DialogTitle>
          </DialogHeader>
          <p>Êtes-vous sûr de vouloir supprimer cette recette? Cette action est irréversible.</p>
          <div className="dialog-actions">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} data-testid="cancel-delete-btn">
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialogue de compatibilité alimentaire */}
      <Dialog open={showCompatibilityDialog} onOpenChange={setShowCompatibilityDialog}>
        <DialogContent data-testid="compatibility-dialog" style={{ maxWidth: '500px' }}>
          <DialogHeader>
            <DialogTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#DC2626' }}>
              <WarningIcon size={24} color="#DC2626" />
              Attention - Incompatibilité détectée
            </DialogTitle>
          </DialogHeader>
          
          {compatibility && (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ color: '#5D6F61', marginBottom: '1rem' }}>
                Cette recette contient des ingrédients qui ne correspondent pas à vos préférences alimentaires:
              </p>
              
              {/* Avertissements */}
              {compatibility.warnings?.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  {compatibility.warnings.map((warning, idx) => (
                    <div key={`warn-${warning.ingredient}-${idx}`} style={{
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      marginBottom: '0.5rem'
                    }}>
                      <p style={{ fontWeight: '600', color: '#DC2626', margin: 0 }}>
                        {warning.ingredient}
                      </p>
                      <p style={{ color: '#7F1D1D', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                        {warning.reason}
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Suggestions de remplacement */}
              {compatibility.suggestions?.length > 0 && (
                <div>
                  <p style={{ fontWeight: '600', color: '#3A4A3E', marginBottom: '0.75rem' }}>
                    💡 Suggestions de remplacement:
                  </p>
                  {compatibility.suggestions.map((suggestion, idx) => (
                    <div key={`sug-${suggestion.original || idx}`} style={{
                      background: '#F0FDF4',
                      border: '1px solid #BBF7D0',
                      borderRadius: '8px',
                      padding: '0.75rem',
                      marginBottom: '0.5rem'
                    }}>
                      <p style={{ margin: 0, color: '#166534' }}>
                        <strong>{suggestion.original}</strong> → <strong>{suggestion.alternative}</strong>
                      </p>
                      {suggestion.note && (
                        <p style={{ color: '#15803D', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
                          {suggestion.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <Button 
              variant="outline" 
              onClick={() => setShowCompatibilityDialog(false)}
              style={{ flex: 1 }}
            >
              J'ai compris
            </Button>
            <Button 
              onClick={() => {
                setShowCompatibilityDialog(false);
                navigate('/profile');
              }}
              style={{ flex: 1, background: '#87A96B' }}
            >
              Modifier mes préférences
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <AdBanner />
      <Footer />
    </div>
  );
}
